import { MenuIcon } from "@xola/icons";
import { useDebounceEffect } from "ahooks";
import React, { useState } from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { AddOn, CatalogItemSequenceType, Experience } from "types";
import { api } from "../../../../api/api";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { getResultingSequence } from "../../../../models/CatalogItem";
import { reorder } from "../../../../utils/reorder";
import { AddOnItem } from "./AddOnItem";

export const ExperienceAddOns = ({ addOns, experience }: { addOns: AddOn[]; experience: Experience }) => {
    const [orderedAddOns, setOrderedAddOns] = useState(addOns);
    const invalidate = useInvalidate();

    useDebounceEffect(
        () => {
            // This useEffect needs to update orderedAddOns state, when we change addOns out of this ExperienceAddOns list.
            // We have local state with ordered addOns and addOns that comes as prop (which can be filtered by type).
            // So, when addOns from prop are changed I update orderedAddons state with addOns from prop,
            // but save order of addons from orderedAddons state (so I just replace addOns in the state
            // with addons from the prop) to avoid addOns flickering on dragging
            const addOnsMap = new Map(addOns.map((addOn) => [addOn.id, addOn]));

            const newOrderedAddOns = orderedAddOns
                .map((orderedAddOn) => (addOnsMap.has(orderedAddOn.id) ? addOnsMap.get(orderedAddOn.id) : null))
                .filter((addOn): addOn is AddOn => addOn !== null);

            for (const addOn of addOns) {
                if (!orderedAddOns.some((orderedAddOn) => orderedAddOn.id === addOn.id)) {
                    newOrderedAddOns.push(addOn);
                }
            }

            setOrderedAddOns(newOrderedAddOns);
        },
        [addOns],
        { leading: true, trailing: true, maxWait: 200 }, // This reduces the number of useEffect calls
    );

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const resultingSequence = getResultingSequence({
            destinationIndex: result.destination.index,
            sourceIndex: result.source.index,
            productCatalogItems: orderedAddOns,
            type: CatalogItemSequenceType.ADDON,
        });

        setOrderedAddOns(reorder(orderedAddOns, result.source.index, result.destination.index));
        const catalogItem = addOns.find((addOn) => addOn.id === result.draggableId);
        await api.put(`/catalogItems/${catalogItem?.id}`, {
            ...catalogItem,
            sequence: resultingSequence,
        });
        invalidate("catalogItems");
        invalidate("experiences", experience.id);
    };

    return (
        <DragDropContext onDragEnd={async (result: DropResult) => handleDragEnd(result)}>
            <div>
                <Droppable droppableId="addOnList">
                    {(provided: DroppableProvided) => (
                        <div ref={provided.innerRef} className="h-full flex-1 rounded" {...provided.droppableProps}>
                            <div className="divide-y divide-gray-light rounded pr-1">
                                {orderedAddOns.map((addOn: AddOn, index) => (
                                    <Draggable key={addOn.id} draggableId={addOn.id} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps}>
                                                <div
                                                    className="flex items-center space-x-4"
                                                    data-testid="experience-addons-item"
                                                >
                                                    <div {...provided.dragHandleProps} data-testid="draggable-Addon">
                                                        <MenuIcon size="medium" />
                                                    </div>
                                                    <AddOnItem key={addOn.id} addOn={addOn} experience={experience} />
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                            </div>
                        </div>
                    )}
                </Droppable>
            </div>
        </DragDropContext>
    );
};
